<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.return_escrow.submit')"
    :hideSubmitButton="true"
    size="xl"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{
      title ?? $t("forwards.deals.return_escrow.title")
    }}</template>

    <template #body>
      <div class="p-1">
        <p
          v-if="commitmentGone"
          class="error-note mb-0"
          role="alert"
          data-testid="escrow-commitment-gone"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ $t("forwards.deals.common.commitment_gone") }}</span>
        </p>

        <template v-else>
          <EscrowContributionsTableComponent
            v-if="contributions !== null"
            :rows="contributions"
            :class="{ 'mb-4': canReturn }"
          />

          <span
            v-if="contributions !== null && canReturn"
            class="eyebrow-label d-block mb-2"
            >{{ $t("forwards.deals.return_escrow.section_title") }}</span
          >

          <LoadingIndicatorComponent
            v-if="canReturn && loading"
            inline
            :message="$t('forwards.deals.return_escrow.loading')"
          />

          <template v-else-if="canReturn && results === null">
            <p class="text-body-tertiary">
              {{ $t("forwards.deals.return_escrow.body") }}
            </p>

            <p v-if="visibleRows.length === 0" class="advisory-note mb-0">
              <FontAwesomeIcon icon="fa-regular fa-circle-info" />
              <span>{{ $t("forwards.deals.return_escrow.nothing") }}</span>
            </p>

            <table v-else class="table table-sm">
              <thead>
                <tr>
                  <th scope="col">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="allSelected"
                      :indeterminate="someSelected"
                      :aria-label="
                        $t('forwards.deals.return_escrow.select_all')
                      "
                      @change="toggleAll"
                    />
                  </th>
                  <th scope="col">
                    {{ $t("forwards.deals.return_escrow.company") }}
                  </th>
                  <th scope="col">
                    {{ $t("forwards.deals.return_escrow.device") }}
                  </th>
                  <th scope="col">
                    {{ $t("forwards.deals.return_escrow.month") }}
                  </th>
                  <th scope="col" class="text-end">
                    {{ $t("forwards.deals.return_escrow.escrowed") }}
                  </th>
                  <th scope="col" class="count-cell">
                    {{ $t("forwards.deals.return_escrow.return_count") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in visibleRows" :key="row.attribution_id">
                  <td>
                    <input
                      v-model="selected[row.attribution_id]"
                      type="checkbox"
                      class="form-check-input"
                      :aria-label="
                        $t('forwards.deals.return_escrow.select_row', {
                          device:
                            row.device_name ?? $t('forwards.common.unknown'),
                        })
                      "
                    />
                  </td>
                  <td>{{ row.company_name }}</td>
                  <td>
                    {{ row.device_name ?? $t("forwards.common.unknown") }}
                  </td>
                  <td class="text-nowrap">{{ row.production_period_label }}</td>
                  <td class="text-end">
                    {{ formatNumberWithSpaces(row.count) }}
                  </td>
                  <td class="count-cell">
                    <MwhInputComponent
                      v-model="counts[row.attribution_id]"
                      size="sm"
                      unit=""
                      :min="1"
                      :max="row.count"
                      :disabled="!selected[row.attribution_id]"
                      :ariaLabel="
                        $t('forwards.deals.return_escrow.count_for', {
                          device:
                            row.device_name ?? $t('forwards.common.unknown'),
                        })
                      "
                    />
                    <div
                      v-if="
                        (counts[row.attribution_id] ?? row.count) > row.count
                      "
                      class="error-note fs-10 mt-1"
                      role="alert"
                    >
                      <span>{{
                        $t("forwards.deals.return_escrow.over_count", {
                          mwh: formatNumberWithSpaces(row.count),
                        })
                      }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="summary.length > 0" class="advisory-note">
              <FontAwesomeIcon icon="fa-regular fa-circle-info" />
              <span>
                {{ $t("forwards.deals.return_escrow.summary_title") }}
                <span v-for="line in summary" :key="line.companyName">
                  · {{ line.companyName }}:
                  {{
                    $t("forwards.common.mwh_value", {
                      value: formatNumberWithSpaces(line.mwh),
                    })
                  }}
                </span>
              </span>
            </div>

            <p v-if="errorMessage" class="error-note mb-0" role="alert">
              <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
              <span>{{ errorMessage }}</span>
            </p>
          </template>

          <template v-else-if="canReturn">
            <p class="fw-semibold">
              {{
                $t("forwards.deals.return_escrow.results_title", {
                  moved: movedCount,
                  failed: failedCount,
                })
              }}
            </p>
            <ul class="list-unstyled mb-0">
              <li
                v-for="(item, index) in results"
                :key="`${item.portfolio_item_id}-${index}`"
                :class="
                  item.ok ? 'text-success-emphasis' : 'text-danger-emphasis'
                "
              >
                {{
                  $t("forwards.deals.return_escrow.result_line", {
                    count: item.count,
                    item: item.portfolio_item_id,
                  })
                }}
                <span v-if="!item.ok"> — {{ item.reason }}</span>
              </li>
            </ul>
          </template>
        </template>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{
          results === null
            ? $t("forwards.deals.common.cancel")
            : $t("forwards.deals.common.close")
        }}
      </button>
      <ActionButtonWithSpinner
        v-if="canReturn && results === null"
        :busy="busy"
        :disabled="!canSubmit"
        variant="btn-primary"
        data-testid="escrow-return-submit"
        @click="submit"
      >
        {{ $t("forwards.deals.return_escrow.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import EscrowContributionsTableComponent from "@/forwards2/deals/EscrowContributionsTableComponent.vue";
import type {
  EscrowAttributionRow,
  EscrowByCompany,
  EscrowMoveItemResult,
} from "@/forwards2/forwardsTypes";
import { sortBy, sumBy } from "lodash-es";
import { computed, ref, watch } from "vue";

/**
 * One commitment's escrow: who supplied it and, for a super admin, the form to hand it back.
 *
 * Opened from a ledger row it first shows the per-company breakdown every viewer may see; the
 * return form below it renders only while `canReturn` (a super admin, on an unreleased
 * commitment) — the attribution list behind it is an admin endpoint. Opened from the free-escrow
 * card there is no commitment, so no breakdown, only the form over the card's own escrow.
 *
 * A commitment that a refresh took off the deal while the modal was open (its backing deleted, the
 * deal cancelled) is said so rather than silently turning the modal into the free-escrow form, and
 * the return stays disabled until the modal is closed.
 *
 * Every row is one attribution and can go back in part, so the counts are editable. The move is
 * bookkeeping only — the certificates stay on Soldera's registry account and are assigned to a new
 * virtual portfolio of each owning company — so nothing can fail per row. The per-item outcome
 * still replaces the form rather than closing it, so ops can read what was handed back, and the
 * failure styling stays because the same result shape carries the pull's per-row registry failures.
 */
const props = withDefaults(
  defineProps<{
    rows: EscrowAttributionRow[];
    /** Set when the modal was opened from one commitment rather than the free-escrow card. */
    commitmentId?: number | null;
    /** The free-escrow card's attributions: what the form offers when there is no commitment. */
    freeAttributionIds?: number[];
    title?: string | null;
    /** The commitment's escrow per company, shown to every viewer; null when there is no commitment. */
    contributions?: EscrowByCompany[] | null;
    canReturn?: boolean;
    /** The commitment the modal was opened for is no longer on the deal. */
    commitmentGone?: boolean;
    loading?: boolean;
    busy?: boolean;
    errorMessage?: string | null;
    results?: EscrowMoveItemResult[] | null;
  }>(),
  {
    commitmentId: null,
    freeAttributionIds: () => [],
    title: null,
    contributions: null,
    canReturn: true,
    commitmentGone: false,
    loading: false,
    busy: false,
    errorMessage: null,
    results: null,
  }
);

const emit = defineEmits<{
  submit: [selection: { attribution_id: number; count: number }[]];
}>();

const model = defineModel<boolean>({ required: true });

const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};
const selected = ref<Record<number, boolean>>({});
const counts = ref<Record<number, number | null>>({});

/**
 * `rows` is every returnable attribution of the deal, escrow a live delivery holds included, and
 * nothing in the table tells the two apart — so the free-escrow card offers its own attributions
 * only. Not "no slice and no delivery": escrow a voided shipment named is free as well.
 */
const visibleRows = computed(() => {
  if (props.commitmentId !== null)
    return props.rows.filter((row) => row.commitment_id === props.commitmentId);
  const free = new Set(props.freeAttributionIds);
  return props.rows.filter((row) => free.has(row.attribution_id));
});

const selection = computed(() =>
  visibleRows.value
    .filter((row) => selected.value[row.attribution_id])
    .map((row) => ({
      attribution_id: row.attribution_id,
      count: counts.value[row.attribution_id] ?? row.count,
    }))
    .filter((entry) => entry.count > 0)
);

const summary = computed(() => {
  const byCompany = new Map<string, number>();
  for (const row of visibleRows.value) {
    const entry = selection.value.find(
      (candidate) => candidate.attribution_id === row.attribution_id
    );
    if (entry === undefined) continue;
    byCompany.set(
      row.company_name,
      (byCompany.get(row.company_name) ?? 0) + entry.count
    );
  }
  return sortBy(
    [...byCompany].map(([companyName, mwh]) => ({ companyName, mwh })),
    (line) => line.companyName
  );
});

const selectedCount = computed(
  () =>
    visibleRows.value.filter((row) => selected.value[row.attribution_id]).length
);
const allSelected = computed(
  () =>
    visibleRows.value.length > 0 &&
    selectedCount.value === visibleRows.value.length
);
const someSelected = computed(
  () => selectedCount.value > 0 && !allSelected.value
);

const overCountRows = computed(() =>
  visibleRows.value.filter(
    (row) =>
      selected.value[row.attribution_id] &&
      (counts.value[row.attribution_id] ?? row.count) > row.count
  )
);

const canSubmit = computed(
  () =>
    !props.busy &&
    !props.commitmentGone &&
    selection.value.length > 0 &&
    overCountRows.value.length === 0
);

const movedCount = computed(() =>
  sumBy(props.results ?? [], (item) => (item.ok ? item.count : 0))
);
const failedCount = computed(
  () => (props.results ?? []).filter((item) => !item.ok).length
);

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) {
    selected.value = {};
    counts.value = {};
  }
});

// Rows arrive after the modal opens; default every count to the whole attribution.
watch(
  visibleRows,
  (rows) => {
    for (const row of rows) {
      if (counts.value[row.attribution_id] === undefined)
        counts.value[row.attribution_id] = row.count;
    }
  },
  { immediate: true }
);

const toggleAll = () => {
  const select = !allSelected.value;
  selected.value = Object.fromEntries(
    visibleRows.value.map((row) => [row.attribution_id, select])
  );
};

const close = () => {
  model.value = false;
};

const submit = () => {
  if (!canSubmit.value) return;
  emit("submit", selection.value);
};
</script>

<style scoped lang="scss">
.count-cell {
  min-width: 8rem;
}
</style>
